/**
 * AiThinkingBanner
 *
 * A slim animated strip that slides in directly above the manuscript prose
 * whenever an AI operation is in progress (generation, tone shift, streaming).
 * Uses an SVG concentric-orbit spinner: three arcs at different radii rotate
 * at different speeds and in alternating directions, giving a planetary feel.
 *
 * Prevents the writer from perceiving silence as a stall and button-mashing.
 */
import React from 'react';
import { cn } from '@/lib/utils';

/* ── Concentric orbit SVG spinner ───────────────────────────────────────── */
function OrbitSpinner() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      {/* Outer arc — slow clockwise, rose */}
      <circle
        cx="10" cy="10" r="8.5"
        stroke="rgba(251,113,133,0.75)"
        strokeWidth="1.4"
        strokeDasharray="21 33"
        strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 10 10"
          to="360 10 10"
          dur="2.8s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Middle arc — medium counter-clockwise, amber/gold */}
      <circle
        cx="10" cy="10" r="5.5"
        stroke="rgba(251,191,36,0.9)"
        strokeWidth="1.4"
        strokeDasharray="13 22"
        strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="180 10 10"
          to="-180 10 10"
          dur="1.7s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Inner arc — fast clockwise, bright rose-white */}
      <circle
        cx="10" cy="10" r="2.8"
        stroke="rgba(255,228,230,0.95)"
        strokeWidth="1.4"
        strokeDasharray="7 11"
        strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 10 10"
          to="360 10 10"
          dur="0.85s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

/* ── Label map ──────────────────────────────────────────────────────────── */
const LABELS = {
  streaming: 'Writing your chapter…',
  tone:      'Shifting the tone…',
  busy:      'Crafting your next passage…',
};

/* ── Component ──────────────────────────────────────────────────────────── */
export function AiThinkingBanner({ busy, toneBusy, isStreaming, streamingWordCount = 0 }) {
  const show  = busy || toneBusy || isStreaming;
  const label = isStreaming ? LABELS.streaming
              : toneBusy   ? LABELS.tone
              :               LABELS.busy;

  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden transition-all duration-300 ease-in-out',
        show ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0 pointer-events-none',
      )}
      data-testid="ai-thinking-banner"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* ── Banner row ── */}
      <div className="relative flex h-8 items-center gap-2.5 overflow-hidden border-b border-rose-900/40 bg-gradient-to-r from-[#2a0a10] via-[#3d1118] to-[#2c1a08] px-3">

        {/* Shimmer sweep — glides across the background */}
        <span className="ai-thinking-shimmer pointer-events-none absolute inset-0" aria-hidden="true" />

        <OrbitSpinner />

        <span className="relative z-10 text-[12px] font-semibold tracking-wide text-rose-100/85">
          {label}
        </span>

        {/* Right side: word count while streaming, pulsing dots otherwise */}
        {isStreaming && streamingWordCount > 0 ? (
          <span
            className="ml-auto relative z-10 tabular-nums text-[11px] text-rose-100/60"
            data-testid="streaming-word-count"
          >
            ~{streamingWordCount.toLocaleString()} words so far
          </span>
        ) : (
          <span className="ml-auto flex items-center gap-[3px]" aria-hidden="true">
            <span className="ai-thinking-dot" style={{ animationDelay: '0s' }} />
            <span className="ai-thinking-dot" style={{ animationDelay: '0.22s' }} />
            <span className="ai-thinking-dot" style={{ animationDelay: '0.44s' }} />
          </span>
        )}
      </div>
    </div>
  );
}
